import React, { useCallback, useEffect, useState } from 'react';
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CFormInput,
    CFormTextarea,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilPlus } from '@coreui/icons';
import * as DomicilappService from '../../services/DomicilappService';
import { useFleet } from '../../contexts/FleetContext';
import { FleetCourier } from '../../types/fleet';
import { showToastTR } from '../../Utils/BaseApp';
import { formatCurrency, getApiErrorMessage } from '../orders/orderUtils';
import { FleetGuard, FleetHeader } from './FleetHeader';
import { CourierStateBadge, formatMinutes } from './fleetUtils';

/* ------------------------------------------------------------------ Vincular: celular -> código SMS -> confirmar */
const LinkCourierModal: React.FC<{ companyId: number; visible: boolean; onClose: () => void; onLinked: () => void }> = ({ companyId, visible, onClose, onLinked }) => {
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [step, setStep] = useState<'phone' | 'code'>('phone');
    const [courierName, setCourierName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) {
            setPhone('');
            setCode('');
            setStep('phone');
            setError(null);
        }
    }, [visible]);

    const requestCode = () => {
        setBusy(true);
        setError(null);
        DomicilappService.requestCourierLink(companyId, phone)
            .then((response) => {
                if (response?.status === 200) {
                    setCourierName(response.data?.name ?? '');
                    setStep('code');
                } else {
                    setError(response?.message ?? 'No fue posible enviar el código');
                }
            })
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible enviar el código')))
            .finally(() => setBusy(false));
    };

    const confirm = () => {
        setBusy(true);
        setError(null);
        DomicilappService.confirmCourierLink(companyId, phone, code)
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR('Domiciliario vinculado a tu flota', 3000, 'success');
                    onLinked();
                    onClose();
                } else {
                    setError(response?.message ?? 'No fue posible vincular al domiciliario');
                }
            })
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible vincular al domiciliario')))
            .finally(() => setBusy(false));
    };

    return (
        <CModal visible={visible} onClose={onClose} alignment="center">
            <CModalHeader>
                <CModalTitle>Vincular domiciliario</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {error && <CAlert color="danger" className="small py-2">{error}</CAlert>}
                {step === 'phone' ? (
                    <>
                        <p className="small text-body-secondary">
                            El domiciliario debe haber creado su cuenta en la app DomicilApp. Le enviaremos un código por SMS a su celular para confirmar que acepta unirse a tu flota.
                        </p>
                        <label className="form-label">Celular del domiciliario</label>
                        <CFormInput value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="3001234567" inputMode="numeric" />
                    </>
                ) : (
                    <>
                        <p className="small text-body-secondary">
                            Enviamos un código a {courierName || 'el domiciliario'} ({phone}). Pídele que te lo dicte.
                        </p>
                        <label className="form-label">Código</label>
                        <CFormInput value={code} onChange={(e) => setCode(e.target.value.trim())} inputMode="numeric" autoFocus />
                    </>
                )}
            </CModalBody>
            <CModalFooter>
                {step === 'code' && <CButton color="light" onClick={() => setStep('phone')}>Cambiar celular</CButton>}
                <CButton color="dark" disabled={busy || (step === 'phone' ? phone.length < 7 : code.length < 4)} onClick={step === 'phone' ? requestCode : confirm}>
                    {busy && <CSpinner size="sm" className="me-1" />}
                    {step === 'phone' ? 'Enviar código' : 'Vincular'}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

/* ------------------------------------------------------------------ Bloquear / desbloquear */
const BlockModal: React.FC<{ companyId: number; courier: FleetCourier | null; onClose: () => void; onSaved: () => void }> = ({ companyId, courier, onClose, onSaved }) => {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const blocking = !courier?.blocked_at;

    useEffect(() => setReason(''), [courier]);

    const save = () => {
        if (!courier) return;
        setBusy(true);
        DomicilappService.blockFleetCourier(companyId, courier.id, blocking, reason.trim() || undefined)
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR(blocking ? `${courier.name} quedó bloqueado` : `${courier.name} quedó habilitado`, 3000, 'success');
                    onSaved();
                    onClose();
                } else {
                    showToastTR(response?.message ?? 'No fue posible actualizar', 3000, 'error');
                }
            })
            .catch(() => showToastTR('No fue posible actualizar', 3000, 'error'))
            .finally(() => setBusy(false));
    };

    return (
        <CModal visible={!!courier} onClose={onClose} alignment="center">
            <CModalHeader>
                <CModalTitle>{blocking ? 'Bloquear' : 'Desbloquear'} a {courier?.name}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {blocking ? (
                    <>
                        <p className="small text-body-secondary">Mientras esté bloqueado no recibirá pedidos. Sus ofertas pendientes pasan al siguiente domiciliario.</p>
                        <label className="form-label">Motivo (opcional)</label>
                        <CFormTextarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej: documentos vencidos" />
                    </>
                ) : (
                    <p className="small text-body-secondary mb-0">Volverá a recibir pedidos cuando esté en turno y con saldo.</p>
                )}
            </CModalBody>
            <CModalFooter>
                <CButton color="light" onClick={onClose}>Cancelar</CButton>
                <CButton color={blocking ? 'danger' : 'dark'} disabled={busy} onClick={save}>
                    {busy && <CSpinner size="sm" className="me-1" />}
                    {blocking ? 'Bloquear' : 'Desbloquear'}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

/* ------------------------------------------------------------------ Página */
const FleetCouriersContent: React.FC = () => {
    const { company } = useFleet();
    const [couriers, setCouriers] = useState<FleetCourier[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [linkOpen, setLinkOpen] = useState(false);
    const [blockTarget, setBlockTarget] = useState<FleetCourier | null>(null);

    const load = useCallback(() => {
        if (!company) return;
        setLoading(true);
        DomicilappService.getFleetCouriers(company.id)
            .then((response) => setCouriers(response?.data ?? []))
            .catch(() => setCouriers([]))
            .finally(() => setLoading(false));
    }, [company]);

    useEffect(() => {
        load();
    }, [load]);

    const term = search.trim().toLowerCase();
    const filtered = couriers.filter((c) => !term || c.name.toLowerCase().includes(term) || (c.phone ?? '').includes(term));
    const minBalance = Number(company?.courier_min_balance ?? 0);

    return (
        <>
            <FleetHeader
                title="Domiciliarios"
                description="Tu flota: estado, saldo y actividad de cada domiciliario"
                actions={
                    <CButton size="sm" color="dark" onClick={() => setLinkOpen(true)}>
                        <CIcon icon={cilPlus} size="sm" className="me-1" /> Vincular domiciliario
                    </CButton>
                }
            />

            <CCard>
                <CCardBody className="p-0">
                    <div className="p-3 border-bottom">
                        <CFormInput size="sm" placeholder="Buscar por nombre o celular..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />
                    </div>
                    <CTable responsive hover align="middle" className="mb-0">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell>Domiciliario</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell>Saldo</CTableHeaderCell>
                                <CTableHeaderCell className="text-center">Entregas hoy</CTableHeaderCell>
                                <CTableHeaderCell>Pedido actual</CTableHeaderCell>
                                <CTableHeaderCell>Última ubicación</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>
                        <CTableBody>
                            {loading ? (
                                <CTableRow><CTableDataCell colSpan={7} className="text-center py-5"><CSpinner size="sm" /></CTableDataCell></CTableRow>
                            ) : filtered.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={7} className="text-center py-5 text-body-secondary">
                                        {couriers.length === 0 ? 'Aún no tienes domiciliarios vinculados.' : 'Sin resultados.'}
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                filtered.map((courier) => (
                                    <CTableRow key={courier.id}>
                                        <CTableDataCell>
                                            <div className="fw-semibold text-body">{courier.name}</div>
                                            <small className="text-body-secondary">{courier.phone}</small>
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CourierStateBadge state={courier.state} />
                                            {courier.blocked_reason && <small className="d-block text-body-secondary">{courier.blocked_reason}</small>}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <span className={courier.balance < minBalance || courier.balance <= 0 ? 'text-danger fw-semibold' : 'text-body'}>{formatCurrency(courier.balance)}</span>
                                        </CTableDataCell>
                                        <CTableDataCell className="text-center text-body">{courier.delivered_today ?? 0}</CTableDataCell>
                                        <CTableDataCell>
                                            {courier.current_delivery
                                                ? <span className="text-body">#{courier.current_delivery.id} · {courier.current_delivery.status_name}</span>
                                                : <span className="text-body-secondary">—</span>}
                                        </CTableDataCell>
                                        <CTableDataCell className="text-body-secondary">
                                            {courier.location_age_minutes !== null ? `hace ${formatMinutes(courier.location_age_minutes)}` : 'Nunca'}
                                        </CTableDataCell>
                                        <CTableDataCell className="text-end">
                                            <CButton size="sm" color="light" onClick={() => setBlockTarget(courier)}>
                                                {courier.blocked_at ? 'Desbloquear' : 'Bloquear'}
                                            </CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))
                            )}
                        </CTableBody>
                    </CTable>
                </CCardBody>
            </CCard>

            {company && <LinkCourierModal companyId={company.id} visible={linkOpen} onClose={() => setLinkOpen(false)} onLinked={load} />}
            {company && <BlockModal companyId={company.id} courier={blockTarget} onClose={() => setBlockTarget(null)} onSaved={load} />}
        </>
    );
};

const FleetCouriersPage: React.FC = () => (
    <FleetGuard>
        <FleetCouriersContent />
    </FleetGuard>
);

export default FleetCouriersPage;
